export const ORDER_STATUS = {
    CREATED:                'CREATED',
    AWAITING_PAYMENT:       'AWAITING_PAYMENT',
    PAYMENT_PRE_AUTHORIZED: 'PAYMENT_PRE_AUTHORIZED',
    PAYMENT_SUCCEEDED:      'PAYMENT_SUCCEEDED',
    PAYMENT_FAILED:         'PAYMENT_FAILED',
    PAYMENT_CANCELLED:      'PAYMENT_CANCELLED',
    PAYMENT_DECLINED:       'PAYMENT_DECLINED',
    PAYMENT_VOIDED:         'PAYMENT_VOIDED',
    AWAITING_PACKING:       'AWAITING_PACKING',
    PACKER_ASSIGNED:        'PACKER_ASSIGNED',
    PACKING_COMPLETE:       'PACKING_COMPLETE',
    PAYMENT_PROCESSING:     'PAYMENT_PROCESSING',
    CAPTURE_FAILED:         'CAPTURE_FAILED',
    READY_FOR_COLLECTION:   'READY_FOR_COLLECTION',
    COLLECTION_COMPLETE:    'COLLECTION_COMPLETE',
    ASSIGN_DRIVER:          'ASSIGN_DRIVER',
    DRIVER_ASSIGNED:        'DRIVER_ASSIGNED',
    ON_ROUTE:               'ON_ROUTE',
    DELIVERY_FAILED:        'DELIVERY_FAILED',
    DELIVERY_COMPLETE:      'DELIVERY_COMPLETE',
    ORDER_CANCELLED:        'ORDER_CANCELLED',
} as const;

export type OrderStatus = typeof ORDER_STATUS[keyof typeof ORDER_STATUS];

export const OPEN_ORDER_STATUSES: OrderStatus[] = [
    ORDER_STATUS.CREATED,
    ORDER_STATUS.AWAITING_PAYMENT,
    ORDER_STATUS.PAYMENT_PRE_AUTHORIZED,
    ORDER_STATUS.PAYMENT_SUCCEEDED,
    ORDER_STATUS.PAYMENT_PROCESSING,
    ORDER_STATUS.PAYMENT_FAILED,
    ORDER_STATUS.PAYMENT_CANCELLED,
    ORDER_STATUS.PAYMENT_DECLINED,
    ORDER_STATUS.PAYMENT_VOIDED,
    ORDER_STATUS.CAPTURE_FAILED,
    ORDER_STATUS.AWAITING_PACKING,
    ORDER_STATUS.PACKER_ASSIGNED,
    ORDER_STATUS.PACKING_COMPLETE,
    ORDER_STATUS.READY_FOR_COLLECTION,
    ORDER_STATUS.ASSIGN_DRIVER,
    ORDER_STATUS.DRIVER_ASSIGNED,
    ORDER_STATUS.ON_ROUTE,
    ORDER_STATUS.DELIVERY_FAILED,
];

export const ARCHIVED_ORDER_STATUSES: OrderStatus[] = [
    ORDER_STATUS.DELIVERY_COMPLETE,
    ORDER_STATUS.COLLECTION_COMPLETE,
    ORDER_STATUS.ORDER_CANCELLED,
    ORDER_STATUS.DELIVERY_FAILED,
];

export const EXCLUDED_DISPATCH_STATUSES: string[] = [
    ORDER_STATUS.PAYMENT_DECLINED,
    ORDER_STATUS.PAYMENT_FAILED,
    ORDER_STATUS.PAYMENT_CANCELLED,
    ORDER_STATUS.PAYMENT_VOIDED,
    ORDER_STATUS.CAPTURE_FAILED,
    ORDER_STATUS.DELIVERY_COMPLETE,
    ORDER_STATUS.DELIVERY_FAILED,
    ORDER_STATUS.ORDER_CANCELLED,
];

export const NON_CANCELLABLE_STATUSES: string[] = [
    ORDER_STATUS.ON_ROUTE,
    ORDER_STATUS.DELIVERY_COMPLETE,
    ORDER_STATUS.DELIVERY_FAILED,
    ORDER_STATUS.ORDER_CANCELLED,
    ORDER_STATUS.COLLECTION_COMPLETE,
];

export const STATUS_LABELS: Record<string, string> = {
    CREATED:                'Created',
    AWAITING_PAYMENT:       'Awaiting Payment',
    PAYMENT_PRE_AUTHORIZED: 'Pre-Authorised',
    PAYMENT_SUCCEEDED:      'Payment Succeeded',
    PAYMENT_FAILED:         'Payment Failed',
    PAYMENT_CANCELLED:      'Payment Cancelled',
    PAYMENT_DECLINED:       'Payment Declined',
    PAYMENT_VOIDED:         'Payment Voided',
    AWAITING_PACKING:       'Awaiting Packing',
    PACKER_ASSIGNED:        'Packer Assigned',
    PACKING_COMPLETE:       'Packing Complete',
    PAYMENT_PROCESSING:     'Payment Processing',
    CAPTURE_FAILED:         'Capture Failed',
    READY_FOR_COLLECTION:   'Ready for Collection',
    COLLECTION_COMPLETE:    'Collection Complete',
    ASSIGN_DRIVER:          'Awaiting Driver',
    DRIVER_ASSIGNED:        'Driver Assigned',
    ON_ROUTE:               'On Route',
    DELIVERY_COMPLETE:      'Delivered',
    DELIVERY_FAILED:        'Delivery Failed',
    ORDER_CANCELLED:        'Cancelled',
};

export const STATUS_OPTIONS: { value: string; label: string }[] = Object.entries(STATUS_LABELS).map(
    ([value, label]) => ({ value, label })
);

export const STATUS_COLORS: Record<string, string> = {
    CREATED:                'bg-stone-100 text-stone-700 ring-stone-200',
    AWAITING_PAYMENT:       'bg-amber-50 text-amber-700 ring-amber-200',
    PAYMENT_PRE_AUTHORIZED: 'bg-sky-50 text-sky-700 ring-sky-200',
    PAYMENT_SUCCEEDED:      'bg-emerald-50 text-emerald-700 ring-emerald-200',
    PAYMENT_FAILED:         'bg-red-50 text-red-700 ring-red-200',
    PAYMENT_CANCELLED:      'bg-red-50 text-red-700 ring-red-200',
    PAYMENT_DECLINED:       'bg-red-50 text-red-700 ring-red-200',
    PAYMENT_VOIDED:         'bg-stone-100 text-stone-500 ring-stone-200',
    AWAITING_PACKING:       'bg-blue-50 text-blue-700 ring-blue-200',
    PACKER_ASSIGNED:        'bg-purple-50 text-purple-700 ring-purple-200',
    PACKING_COMPLETE:       'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200',
    PAYMENT_PROCESSING:     'bg-amber-50 text-amber-700 ring-amber-200',
    CAPTURE_FAILED:         'bg-rose-50 text-rose-700 ring-rose-200',
    READY_FOR_COLLECTION:   'bg-teal-50 text-teal-700 ring-teal-200',
    COLLECTION_COMPLETE:    'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]',
    ASSIGN_DRIVER:          'bg-indigo-50 text-indigo-700 ring-indigo-200',
    DRIVER_ASSIGNED:        'bg-indigo-50 text-indigo-700 ring-indigo-200',
    ON_ROUTE:               'bg-orange-50 text-orange-700 ring-orange-200',
    DELIVERY_COMPLETE:      'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]',
    DELIVERY_FAILED:        'bg-rose-50 text-rose-700 ring-rose-200',
    ORDER_CANCELLED:        'bg-rose-50 text-rose-700 ring-rose-200',
};

export const STATUS_ICONS: Record<string, { bg: string; ring: string; dot: string; icon: string }> = {
    CREATED:                { bg: 'bg-blue-50',    ring: 'ring-blue-200',    dot: 'bg-blue-500',    icon: '📦' },
    AWAITING_PAYMENT:       { bg: 'bg-amber-50',   ring: 'ring-amber-200',   dot: 'bg-amber-500',   icon: '💳' },
    PAYMENT_PRE_AUTHORIZED: { bg: 'bg-sky-50',     ring: 'ring-sky-200',     dot: 'bg-sky-500',     icon: '🔒' },
    PAYMENT_SUCCEEDED:      { bg: 'bg-emerald-50', ring: 'ring-emerald-200', dot: 'bg-emerald-500', icon: '✓' },
    PAYMENT_FAILED:         { bg: 'bg-red-50',     ring: 'ring-red-200',     dot: 'bg-red-500',     icon: '✕' },
    PAYMENT_DECLINED:       { bg: 'bg-red-50',     ring: 'ring-red-200',     dot: 'bg-red-600',     icon: '✕' },
    PAYMENT_CANCELLED:      { bg: 'bg-gray-50',    ring: 'ring-gray-200',    dot: 'bg-gray-500',    icon: '✕' },
    PAYMENT_VOIDED:         { bg: 'bg-stone-50',   ring: 'ring-stone-200',   dot: 'bg-stone-400',   icon: '∅' },
    AWAITING_PACKING:       { bg: 'bg-purple-50',  ring: 'ring-purple-200',  dot: 'bg-purple-500',  icon: '📋' },
    PACKER_ASSIGNED:        { bg: 'bg-pink-50',    ring: 'ring-pink-200',    dot: 'bg-pink-500',    icon: '👤' },
    PACKING_COMPLETE:       { bg: 'bg-cyan-50',    ring: 'ring-cyan-200',    dot: 'bg-cyan-500',    icon: '✓' },
    PAYMENT_PROCESSING:     { bg: 'bg-amber-50',   ring: 'ring-amber-200',   dot: 'bg-amber-400',   icon: '⏳' },
    CAPTURE_FAILED:         { bg: 'bg-rose-50',    ring: 'ring-rose-200',    dot: 'bg-rose-500',    icon: '✕' },
    READY_FOR_COLLECTION:   { bg: 'bg-teal-50',    ring: 'ring-teal-200',    dot: 'bg-teal-500',    icon: '🏪' },
    COLLECTION_COMPLETE:    { bg: 'bg-green-50',   ring: 'ring-green-200',   dot: 'bg-green-600',   icon: '🛍️' },
    ASSIGN_DRIVER:          { bg: 'bg-teal-50',    ring: 'ring-teal-200',    dot: 'bg-teal-500',    icon: '🔍' },
    DRIVER_ASSIGNED:        { bg: 'bg-sky-50',     ring: 'ring-sky-200',     dot: 'bg-sky-500',     icon: '👤' },
    ON_ROUTE:               { bg: 'bg-lime-50',    ring: 'ring-lime-200',    dot: 'bg-lime-500',    icon: '🚚' },
    DELIVERY_FAILED:        { bg: 'bg-orange-50',  ring: 'ring-orange-200',  dot: 'bg-orange-500',  icon: '⚠' },
    DELIVERY_COMPLETE:      { bg: 'bg-green-50',   ring: 'ring-green-200',   dot: 'bg-green-600',   icon: '✓' },
    ORDER_CANCELLED:        { bg: 'bg-slate-50',   ring: 'ring-slate-200',   dot: 'bg-slate-500',   icon: '✕' },
};

export const STATUS_ICONS_DEFAULT = { bg: 'bg-gray-50', ring: 'ring-gray-200', dot: 'bg-gray-500', icon: '•' };

export const PICKING_SESSION_STATUS_COLORS: Record<string, string> = {
    COMPLETED:   'bg-emerald-50 text-emerald-700 ring-emerald-200',
    IN_PROGRESS: 'bg-amber-50 text-amber-700 ring-amber-200',
    CANCELLED:   'bg-red-50 text-red-700 ring-red-200',
};

export const STATUS_DESCRIPTIONS: Record<string, string> = {
    CREATED:                'Order record created in the system. Awaiting payment.',
    AWAITING_PAYMENT:       'Order is pending. Waiting for payment confirmation from the gateway.',
    PAYMENT_PRE_AUTHORIZED: 'Funds have been reserved on the customer\'s card. Awaiting capture.',
    PAYMENT_SUCCEEDED:      'Payment verified. Order confirmed and moved to the packing queue.',
    PAYMENT_FAILED:         'Payment attempt failed. System awaiting a retry or manual intervention.',
    PAYMENT_DECLINED:       'Transaction declined by the bank. Customer may need to check card limits.',
    PAYMENT_CANCELLED:      'Payment session was closed or cancelled before completion.',
    PAYMENT_VOIDED:         'Pre-authorisation was voided before capture. No funds were taken.',
    AWAITING_PACKING:       'Order is ready. Waiting for a warehouse packer to begin.',
    PACKER_ASSIGNED:        'Packer has accepted the task and is currently preparing the items.',
    PACKING_COMPLETE:       'Items have been verified and packed. Ready for driver assignment.',
    PAYMENT_PROCESSING:     'Capture request submitted to the gateway. Awaiting final confirmation.',
    CAPTURE_FAILED:         'Capture of the pre-authorised funds failed. Manual review required.',
    READY_FOR_COLLECTION:   'Items packed and ready for collection. Customer has been notified.',
    COLLECTION_COMPLETE:    'Customer collected their order. Handover confirmed by staff.',
    ASSIGN_DRIVER:          'Order is in the dispatch pool. Searching for available nearby drivers.',
    DRIVER_ASSIGNED:        'Driver has been assigned and the order is now visible in the driver app.',
    ON_ROUTE:               'Driver has collected the order and is currently en route to the destination.',
    DELIVERY_FAILED:        'Delivery attempt unsuccessful. Order marked for return or reschedule.',
    DELIVERY_COMPLETE:      'Handover successful. Order marked as completed and archived.',
    ORDER_CANCELLED:        'Order has been voided. All associated tasks have been terminated.',
};

// ── Payment status colours ─────────────────────────────────────────────────────
//
// Payment gateway statuses live on the `payments` table and have their own
// lifecycle, separate from ORDER_STATUS. These badge colours are used by
// PaymentsTab and any other component that renders a payment status pill.

export const PAYMENT_STATUS_COLORS: Record<string, string> = {
    // Success states
    SUCCESS:                'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]',
    COMPLETED:              'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]',
    PAYMENT_SUCCEEDED:      'bg-[#f0f7e6] text-[#5a7f28] ring-[#c5e08a]',
    // Pending / in-flight states
    PENDING:                'bg-amber-50 text-amber-700 ring-amber-200',
    PAYMENT_PRE_AUTHORIZED: 'bg-sky-50 text-sky-700 ring-sky-200',
    PAYMENT_PROCESSING:     'bg-amber-50 text-amber-700 ring-amber-200',
    AWAITING_PAYMENT:       'bg-amber-50 text-amber-700 ring-amber-200',
    // Failure / void states
    FAILED:                 'bg-rose-50 text-rose-700 ring-rose-200',
    DECLINED:               'bg-rose-50 text-rose-700 ring-rose-200',
    PAYMENT_FAILED:         'bg-rose-50 text-rose-700 ring-rose-200',
    PAYMENT_DECLINED:       'bg-rose-50 text-rose-700 ring-rose-200',
    PAYMENT_CANCELLED:      'bg-red-50 text-red-700 ring-red-200',
    PAYMENT_VOIDED:         'bg-stone-100 text-stone-500 ring-stone-200',
    CAPTURE_FAILED:         'bg-rose-50 text-rose-700 ring-rose-200',
};

export const DEFAULT_PAYMENT_STATUS_COLOR = 'bg-stone-50 text-stone-700 ring-stone-200';

export function getPaymentStatusBadgeColor(status: string): string {
    return PAYMENT_STATUS_COLORS[status?.toUpperCase()] ?? DEFAULT_PAYMENT_STATUS_COLOR;
}